import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { fitText } from "../utils/textFitUtils";
import { useWheelSettings } from "../context/WheelSettingsContext";
import { getNormalizedSpinProgress } from "../config/wheelConfig";
import { pickQuagganEmoji } from "../utils/quagganEmojiPool";
import { buildWheelEntries, getSliceOffset, selectWheelWinnerFromEntries } from "../utils/wheelSimulation";

// TODO: make pointer its own component with extra logic such as animations, sounds, etc.

function Wheel({
  names,
  getNameEmojiSeed,
  getNameIconSrc,
  spinning,
  onSpinEnd,
  winnerIndex,
  setWinnerIndex,
  showWinnerMenu,
  muted,
}) {
  const { settings } = useWheelSettings();
  const wheelEntries = useMemo(
    () => buildWheelEntries(names, Boolean(settings?.clusterNames)),
    [names, settings?.clusterNames],
  );
  const [rotation, setRotation] = useState(0);
  const rotationRef = useRef(0);
  const animationRef = useRef();
  const winnerTimeoutRef = useRef();
  const pointerAudioRef = useRef(null);
  const mutedRef = useRef(muted);
  const lastEdgeIndexRef = useRef(null);
  const onSpinEndRef = useRef(onSpinEnd);

  // Keep ref in sync with state
  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  // Keep latest onSpinEnd handler in a ref so that
  // changes in the parent don't restart the spin effect
  useEffect(() => {
    onSpinEndRef.current = onSpinEnd;
  }, [onSpinEnd]);

  // Keep muted ref and underlying audio element in sync without
  // tying the spin animation logic to React re-renders
  useEffect(() => {
    mutedRef.current = muted;
    if (pointerAudioRef.current) {
      pointerAudioRef.current.muted = muted;
    }
  }, [muted]);

  const getWinningIndex = useCallback(
    (rot) => {
      if (wheelEntries.length === 0) return -1;
      const normalized = (360 - (rot % 360) + 90) % 360;
      return wheelEntries.findIndex((entry, index) => {
        const start = entry.angleStart;
        const end = entry.angleStart + entry.angleSpan;
        if (index === wheelEntries.length - 1) {
          return normalized >= start && normalized <= end;
        }
        return normalized >= start && normalized < end;
      });
    },
    [wheelEntries],
  );

  // Idle spin when not spinning and winner menu is not up
  useEffect(() => {
    if (spinning || wheelEntries.length === 0 || showWinnerMenu) return;
    const interval = setInterval(() => setRotation((r) => r + 0.5), 20);
    return () => clearInterval(interval);
  }, [spinning, wheelEntries.length, showWinnerMenu]);

  // Spinning animation logic
  useEffect(() => {
    if (!spinning || wheelEntries.length === 0) return;

    const spins =
      spinning && typeof spinning.spins === "number"
        ? spinning.spins
        : settings.minSpins;

    const selectedWinner = selectWheelWinnerFromEntries({
      names,
      wheelEntries,
      winnerIndex,
    });

    if (!selectedWinner) return;

    const { winnerEntryIndex, rawIndex: winnerRawIndex, winnerEntry } = selectedWinner;

    if (typeof winnerIndex !== "number" && setWinnerIndex) {
      setWinnerIndex(winnerRawIndex);
    }

    const sliceAngle = winnerEntry.angleSpan;
    const startRotation = rotationRef.current;
    const startNorm = ((startRotation % 360) + 360) % 360;

    const randomOffset = getSliceOffset(sliceAngle, settings.edgeBias);
    const winnerNormalizedAngle = winnerEntry.angleStart + randomOffset;
    const winnerAngle = ((450 - winnerNormalizedAngle) % 360 + 360) % 360;

    let delta = winnerAngle - startNorm;
    if (delta < 0) delta += 360;

    const totalRotation = spins * 360 + delta;
    const targetRotation = startRotation + totalRotation;

    const duration =
      spinning && typeof spinning.duration === "number"
        ? spinning.duration
        : settings.minDurationMs;
    const decelerationCoefficient = settings.decelerationCoefficient;
    const spinSoundMuteDurationMs = settings.spinSoundMuteDurationMs;
    const totalTime = duration;

    const startTime = performance.now();

    lastEdgeIndexRef.current = getWinningIndex(startRotation);

    function animate(now) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / totalTime, 1);

      const eased = getNormalizedSpinProgress(t, decelerationCoefficient);
      const current = startRotation + totalRotation * eased;

      setRotation(current);
      rotationRef.current = current;

      const currentIndex = getWinningIndex(current);
      if (
        elapsed >= spinSoundMuteDurationMs &&
        lastEdgeIndexRef.current !== null &&
        currentIndex !== lastEdgeIndexRef.current &&
        pointerAudioRef.current &&
        !mutedRef.current
      ) {
        try {
          pointerAudioRef.current.currentTime = 0;
          pointerAudioRef.current.play();
        } catch (e) {}
      }
      lastEdgeIndexRef.current = currentIndex;

      if (elapsed < totalTime) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setRotation(targetRotation);
        rotationRef.current = targetRotation;

        winnerTimeoutRef.current = setTimeout(() => {
          if (onSpinEndRef.current) {
            const publishEntries = wheelEntries.map((entry, index) => {
              const firstSourceIndex = entry.sourceIndexes[0] ?? index;
              return {
                id: entry.id,
                name: entry.name,
                label: entry.name,
                count: entry.count,
                angleStart: entry.angleStart,
                angleSpan: entry.angleSpan,
                iconLookupName: entry.name,
                emojiSeed: getNameEmojiSeed
                  ? getNameEmojiSeed(entry.name, firstSourceIndex)
                  : `${entry.name}-${firstSourceIndex}`,
              };
            });

            onSpinEndRef.current({
              entryIndex: winnerEntryIndex,
              rawIndex: winnerRawIndex,
              name: winnerEntry.name,
              winnerEntryIndex,
              publishEntries,
            });
          }
        }, settings.winnerDelayMs);
      }
    }

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      clearTimeout(winnerTimeoutRef.current);
    };
    // rotation intentionally NOT in deps — we use rotationRef instead
    // Avoid depending directly on onSpinEnd to prevent restarts on parent re-renders
    // Settings are read at spin start; changes mid-spin should not restart the animation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, getWinningIndex, names.length, setWinnerIndex, winnerIndex, settings, wheelEntries]);

  useEffect(() => {
    setRotation(0);
    rotationRef.current = 0;
  }, [wheelEntries.length, settings?.clusterNames]);

  const currentWinningIndex = getWinningIndex(rotation);
  const currentWinningEntry = currentWinningIndex >= 0 ? wheelEntries[currentWinningIndex] : null;
  const currentWinningLabel = currentWinningEntry
    ? (currentWinningEntry.count > 1
      ? `${currentWinningEntry.name} x${currentWinningEntry.count}`
      : currentWinningEntry.name)
    : "";

  const { display: winnerDisplay, fontSize: winnerFontSize } = fitText({
    text: currentWinningLabel,
    maxFontSize: 28,
    minFontSize: 14,
    maxWidth: 180,
    maxLength: 18,
    fontScale: 0.6,
  });

  const getSliceTextProps = (name, sliceAngle, radius) => {
    const maxFontSize = 32;
    const minFontSize = 8;
    if (!name) return { fontSize: minFontSize, lines: [] };

    const words = name.split(" ");

    let fontSize = Math.min(
      maxFontSize,
      Math.max(
        minFontSize,
        (2 * Math.PI * radius * (sliceAngle / 360)) /
          Math.max(...words.map((w) => w.length * 0.6)),
      ),
    );

    const sliceAngleRad = (sliceAngle * Math.PI) / 180;
    const maxHeight = 2 * radius * Math.sin(sliceAngleRad / 2);
    const maxLines = Math.max(1, Math.floor(maxHeight / fontSize));

    const lines = [];
    let currentLine = "";
    const maxLineLen = 14;
    for (const word of words) {
      const testLine = currentLine ? currentLine + " " + word : word;
      if (testLine.length <= maxLineLen) currentLine = testLine;
      else {
        lines.push(
          currentLine.length > maxLineLen
            ? currentLine.slice(0, maxLineLen - 1) + "…"
            : currentLine,
        );
        currentLine = word;
      }
    }
    if (currentLine)
      lines.push(
        currentLine.length > maxLineLen
          ? currentLine.slice(0, maxLineLen - 1) + "…"
          : currentLine,
      );

    if (lines.length > maxLines) {
      const truncated = lines.slice(0, maxLines);
      const last = truncated[maxLines - 1];
      truncated[maxLines - 1] =
        last.length > 3 ? last.slice(0, maxLineLen - 1) + "…" : last;
      return { fontSize, lines: truncated };
    }

    return { fontSize, lines };
  };

  return (
    <div className="panel wheel-panel" style={{ position: "relative" }}>
      <audio
        ref={pointerAudioRef}
        src={require("../sounds/coo.ogg")}
        preload="auto"
      />
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 600 600"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", margin: "0 auto" }}
      >
        <defs>
          <radialGradient id="wheelBg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(20, 70, 120, 0.6)" />
            <stop offset="100%" stopColor="rgba(5, 25, 60, 0.75)" />
          </radialGradient>
          <filter id="glowBlue" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Wheel background */}
        <circle cx="300" cy="300" r="240" fill="url(#wheelBg)" />

        {/* Outer ring */}
        <circle
          cx="300"
          cy="300"
          r="240"
          fill="none"
          stroke="rgba(80, 180, 255, 0.45)"
          strokeWidth="3"
        />

        {/* Inner decorative ring */}
        <circle
          cx="300"
          cy="300"
          r="236"
          fill="none"
          stroke="rgba(100, 200, 255, 0.15)"
          strokeWidth="1"
        />

        {/* Pointer */}
        <polygon
          points="540,300 572,288 572,312"
          fill="#40c8ff"
          stroke="#0080c0"
          strokeWidth="2"
          style={{ filter: "drop-shadow(0 0 8px rgba(0, 180, 255, 0.9))" }}
        />

        {wheelEntries.length > 0 ? (
          <g
            style={{
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "300px 300px",
            }}
          >
            {wheelEntries.map((entry, idx) => {
              const displayName = entry.name;
              const angle = entry.angleStart;
              const sliceAngle = entry.angleSpan;
              const centerAngle = angle + sliceAngle / 2;
              const radians = ((centerAngle - 90) * Math.PI) / 180;
              const showClusterCount = entry.count > 1;
              const textRadius = showClusterCount ? 122 : 135;
              const x = 300 + textRadius * Math.cos(radians);
              const y = 300 + textRadius * Math.sin(radians);
              const radialAngle = centerAngle - 90;
              const normalizedScreenTextAngle =
                ((radialAngle + rotation) % 360 + 360) % 360;
              const shouldFlipText =
                normalizedScreenTextAngle > 90 &&
                normalizedScreenTextAngle < 270;
              const textRotation = radialAngle + (shouldFlipText ? 180 : 0);
              const isWinner = idx === currentWinningIndex;
              const firstSourceIndex = entry.sourceIndexes[0] ?? idx;
              const iconSrc = getNameIconSrc ? getNameIconSrc(entry.name, firstSourceIndex) : "";
              const emojiSrc = iconSrc
                ? iconSrc
                : settings.showQuagganEmojis
                  ? pickQuagganEmoji({
                      type: "all",
                      seed: getNameEmojiSeed
                        ? getNameEmojiSeed(entry.name, firstSourceIndex)
                        : `${entry.name}-${firstSourceIndex}`,
                    })
                  : "";
              const emojiRadius = showClusterCount ? 204 : 190;
              const emojiSize = 40;
              const emojiX = 300 + emojiRadius * Math.cos(radians);
              const emojiY = 300 + emojiRadius * Math.sin(radians);
              const { fontSize: sliceFontSize, lines } = getSliceTextProps(
                displayName,
                sliceAngle,
                textRadius,
              );

              return (
                <g key={entry.id}>
                  {isWinner && (
                    <path
                      d={`M300,300 L${300 + 240 * Math.cos(((angle - 90) * Math.PI) / 180)},${300 + 240 * Math.sin(((angle - 90) * Math.PI) / 180)} A240,240 0 ${sliceAngle > 180 ? 1 : 0},1 ${300 + 240 * Math.cos(((angle + sliceAngle - 90) * Math.PI) / 180)},${300 + 240 * Math.sin(((angle + sliceAngle - 90) * Math.PI) / 180)} Z`}
                      fill="rgba(60, 180, 255, 0.15)"
                    />
                  )}
                  <line
                    x1={300}
                    y1={300}
                    x2={300 + 240 * Math.cos(((angle - 90) * Math.PI) / 180)}
                    y2={300 + 240 * Math.sin(((angle - 90) * Math.PI) / 180)}
                    stroke="rgba(100, 200, 255, 0.25)"
                    strokeWidth="1"
                  />
                  {emojiSrc ? (
                    <image
                      href={emojiSrc}
                      xlinkHref={emojiSrc}
                      x={emojiX - emojiSize / 2}
                      y={emojiY - emojiSize / 2}
                      width={emojiSize}
                      height={emojiSize}
                      preserveAspectRatio="xMidYMid meet"
                      transform={`rotate(${-rotation} ${emojiX} ${emojiY})`}
                    />
                  ) : null}
                  <text
                    x={x}
                    // Slight downward offset so text sits nicer in the slice
                    y={
                      y -
                      ((lines.length - 1) / 2) * sliceFontSize +
                      sliceFontSize * 0.2
                    }
                    fill={isWinner ? "#40e8ff" : "rgba(200, 235, 255, 0.9)"}
                    fontSize={sliceFontSize}
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    transform={`rotate(${textRotation} ${x} ${y})`}
                    filter={isWinner ? "url(#glowBlue)" : undefined}
                    style={isWinner ? { fontWeight: "bold" } : {}}
                  >
                    {lines.map((line, i) => (
                      <tspan key={i} x={x} dy={i === 0 ? 0 : sliceFontSize}>
                        {line}
                      </tspan>
                    ))}
                    {showClusterCount ? (
                      <tspan
                        x={x}
                        dy={lines.length ? Math.max(8, Math.min(14, sliceFontSize * 0.58)) : 0}
                        fill={isWinner ? "#9af7ff" : "rgba(205, 238, 255, 0.95)"}
                        fontSize={Math.max(10, Math.min(14, sliceFontSize * 0.68))}
                        style={{ fontWeight: 800 }}
                      >
                        x{entry.count}
                      </tspan>
                    ) : null}
                  </text>
                </g>
              );
            })}
          </g>
        ) : (
          <text
            x="300"
            y="300"
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize="32"
            fill="rgba(180, 220, 255, 0.7)"
          >
            Add names to start
          </text>
        )}
      </svg>

      {/* Winner label */}
      {wheelEntries.length > 0 && !spinning && !showWinnerMenu && (
        <div
          style={{
            position: "absolute",
            left: "calc(95.333% + 6px)",
            top: "50%",
            transform: "translateY(-50%)",
            width: 180,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: winnerFontSize,
            fontWeight: "bold",
            color: "#40e8ff",
            background: "rgba(10, 30, 60, 0.85)",
            borderRadius: 12,
            border: "1px solid rgba(100, 180, 255, 0.35)",
            boxShadow: "0 2px 16px rgba(0, 180, 255, 0.3)",
            backdropFilter: "blur(8px)",
            pointerEvents: "none",
            userSelect: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {winnerDisplay}
        </div>
      )}
    </div>
  );
}

export default Wheel;
